





















































































import Vue from "vue";
import {
  EXERCISE_TYPE,
  ROUTES,
  VUEX_ASSIGNMENT_ACTION_NAMES,
  VUEX_ASSIGNMENT_NAMESPACE,
  VUEX_ROOT_ACTION_NAMES,
  VUEX_ROOT_GETTER_NAMES
} from "@tt-app/constants";
import { exerciseNavHandler } from "@tt-app/utils/exercise/common/handler/nav";
import { ExerciseNavState, ExerciseType, PayloadDrillTranslationAnswer } from "@tt-app/models";
import { shuffleArray } from "@tt-app/utils/shuffleArray";
import { FlatExerciseListDetails, getAuthInfo, nextExerciseRouter } from "@tt-app/utils";
import { api } from "@tt-app/api";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";

import AssignmentCompletionModal from "@tt-app/components/assignment/AssignmentCompletionModal.vue";
import ExerciseHeaderTitle from "@tt-app/components/exercise/ExerciseHeaderTitle.vue";
import ExerciseScope from "@tt-app/components/exercise/ExerciseScope.vue";
import ExerciseScopeButtons from "@tt-app/components/exercise/ExerciseScopeButtons.vue";
import ExerciseTrophy from "@tt-app/components/exercise/common/ExerciseTrophy.vue";
import FeedbackPopup from "@tt-app/components/feedback/FeedbackPopup.vue";
import LayoutExercise from "@tt-app/layouts/LayoutExercise.vue";
import PracticeModeToast from "@tt-app/components/exercise/common/PracticeModeToast.vue";
import ReviewButtons from "@tt-app/components/exercise/drill/ReviewButtons.vue";
import ReviewComposition from "@tt-app/components/exercise/drill/ReviewComposition.vue";
import TranslationQuestion from "@tt-app/components/exercise/drill/TranslationQuestion.vue";
import TranslationButtons from "@tt-app/components/exercise/drill/TranslationButtons.vue";
import {
  LimitedModeDetails,
  Session,
  TerratalkLiveGetterNames,
  TerratalkLiveNamespace
} from "@tt-app/store/terratalkLive/types";
import { audioContext } from "@tt-app/utils/audio/audioContext";

const { DETAILS_REQUEST } = VUEX_ASSIGNMENT_ACTION_NAMES;
const { FETCH_DETAILS } = VUEX_ROOT_ACTION_NAMES;
const { FLAT_EXERCISE_LIST } = VUEX_ROOT_GETTER_NAMES;

interface DrillTranslationExercise {
  course: {
    title: string;
    uuid: string;
  };
  exercise: {
    title: string;
  };
}

interface DrillTranslationPage {
  body: DrillTranslationPageBody[];
  header: string[];
  type: string;
}

interface DrillTranslationPageBody {
  learnedStatus: number;
  problemUuid: string;
  prompt: string;
  responseNumWords: number;
  type: string;
  uuid: string;
}

interface Data {
  answer: string;
  answers: any;
  audioDecoderPlayer: AudioDecoderPlayer;
  currentPageIndex: number;
  chapter: any;
  course: any;
  exercise: any;
  exerciseResult: any;
  exerciseType: ExerciseType;
  feedbackFormVisible: boolean;
  limitedModeActive: boolean;
  learnedBadgeCount: number;
  logId?: number;
  section: any;
  nav: ExerciseNavState;
  practiceMode: boolean;
  processing: boolean;
  selectedUuids: string[];
  showPracticeModeToast: boolean;
}

interface Methods {
  fetchAssignmentDetails: () => void;
  continueToReview: () => void;
  displayPracticeModeToast: () => void;
  dynamicComponent: (componentType: DrillTranslationPage) => string;
  endExercise: () => void;
  fetchDetails: () => void;
  handleFeedbackClose: () => void;
  incrementPage: () => void;
  loadExercise: () => void;
  logAnswer: ({ skipped }: { skipped: boolean }) => void;
  onCloseIconClick: () => void;
  onNextExercise: () => void;
  onExitCancelled: () => void;
  onExitConfirmed: () => void;
  onNextQuestion: () => void;
  onUserSkip: () => void;
  onUserResponse: () => void;
  refreshAudioPlayer: () => void;
  registerExercisePractice: (
    { problemUuids }?: { problemUuids: string[] },
    tryAgain?: boolean
  ) => void;
  resetState: (complete?: boolean) => void;
  startExercise: ({ problemUuids }?: { problemUuids: string[] }) => void;
  startPractice: ({ problemUuids }?: { problemUuids: string[] }, tryAgain?: boolean) => void;
  startTest: (tryAgain: boolean) => void;
  onTryAgain: () => void;
  updateSelectedUuids: (uuids: string[]) => void;
  updateAnswer: (userAnswer: string) => void;
}

interface Computed {
  allCorrect: boolean;
  assignmentId?: string;
  assignmentsCompleted: number;
  courseUuid: string;
  currentExerciseListIndex: number | null;
  currentPage?: DrillTranslationPageBody[];
  currentQuestionAnswered: boolean;
  currentQuestionNumber?: number;
  disabled: boolean;
  drill: any;
  exerciseUuid: string;
  feedbackRouteParams?: {
    withFeedback: string;
    logId: string;
  };
  finalQuestion: boolean;
  flatExerciseList: FlatExerciseListDetails[];
  footer?: string;
  header: any;
  incorrectlyAnsweredQuestionUuids: any[];
  isReviewPage: boolean;
  limitedModeDetails: LimitedModeDetails;
  nextExercise?: FlatExerciseListDetails;
  numCorrectAnswers: number;
  numIncorrectAnswers: number;
  numSkippedQuestions: number;
  pages?: DrillTranslationPage[];
  questions: DrillTranslationPage[];
  questionsByUuid: any;
  scope: any;
  session: Session;
  showFeedbackForm: boolean;
  totalQuestions: number;
  trophy: any;
}

export default Vue.extend<Data, Methods, Computed, any>({
  name: "DrillTranslation",

  components: {
    AssignmentCompletionModal,
    FeedbackPopup,
    ExerciseHeaderTitle,
    ExerciseScope,
    ExerciseScopeButtons,
    ExerciseTrophy,
    LayoutExercise,
    PracticeModeToast,
    ReviewButtons,
    ReviewComposition,
    TranslationQuestion,
    TranslationButtons
  },

  async created() {
    this.fetchDetails();
    await this.loadExercise();
    await this.startExercise();
    document.addEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeDestroy() {
    this.audioDecoderPlayer.destroy();
    document.removeEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  async beforeRouteLeave(to, from, next) {
    exerciseNavHandler.beforeRouteLeave(this.nav, state => (this.nav = state), to, from, next);
  },

  data() {
    const fromAssignment = this.$store.state.navigation.assignmentDetails.entryRoute;

    return {
      answer: "",
      answers: {},
      audioDecoderPlayer: new AudioDecoderPlayer({ audioContext }),
      chapter: undefined,
      course: undefined,
      currentPageIndex: 0,
      exercise: undefined,
      exerciseResult: undefined,
      exerciseType: EXERCISE_TYPE.DRILL_TRANSLATION,
      learnedBadgeCount: 0,
      logId: undefined,
      limitedModeActive: false,
      nav: exerciseNavHandler.getNewState({
        name:
          (fromAssignment || this.$route.params.assignmentId) &&
          this.$store.state.assignment.details
            ? ROUTES.ASSIGNMENT.DETAILS.NAME
            : ROUTES.COURSE.DETAILS.NAME,
        params: { courseUuid: this.$route.params.courseUuid }
      }),
      practiceMode: false,
      processing: false,
      section: undefined,
      selectedUuids: [],
      feedbackFormVisible: true,
      showPracticeModeToast: false
    };
  },

  methods: {
    continueToReview() {
      this.incrementPage();
    },

    displayPracticeModeToast() {
      this.showPracticeModeToast = true;

      window.setTimeout(() => {
        this.showPracticeModeToast = false;
      }, 5000);
    },

    dynamicComponent(component) {
      switch (component.type) {
        case "EXERCISE_SCOPE":
          return "exercise-scope";
        case "DRILL_TRANSLATION":
          return "translation-question";
        case "REVIEW":
          return "review-composition";
        default:
          return "";
      }
    },

    async endExercise() {
      const authInfo = await getAuthInfo(this.$store.getters);

      const params = {
        ...authInfo,
        assignmentId: Number(this.assignmentId) || undefined,
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exerciseType,
        logId: this.logId!,
        numCorrectAnswers: this.numCorrectAnswers,
        numIncorrectAnswers: this.numIncorrectAnswers,
        numSkipped: this.numSkippedQuestions
      };

      this.exerciseResult = await api.exercise.endExercise(params);

      this.nav.requireExitConfirmation = false;
    },

    async fetchAssignmentDetails() {
      await this.$store.dispatch(`${VUEX_ASSIGNMENT_NAMESPACE}/${DETAILS_REQUEST}`, {
        assignmentId: this.$route.params.assignmentId
      });
    },

    fetchDetails() {
      this.$store.dispatch(`${FETCH_DETAILS}`, {
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },

    handleFeedbackClose() {
      this.feedbackFormVisible = false;
    },

    incrementPage() {
      this.answer = "";
      this.currentPageIndex += 1;
    },

    async loadExercise() {
      const authInfo = await getAuthInfo(this.$store.getters);
      const details = {
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exerciseType as ExerciseType
      };

      const apiPayload = { ...authInfo, ...details };

      const data: any = await api.exercise.getQuizBasedExercise(apiPayload);

      this.chapter = data.chapter;
      this.course = data.course;
      this.exercise = data.exercise;
      this.section = data.section;
    },

    async logAnswer({ skipped }) {
      if (!this.currentPage || !this.logId) {
        return;
      }

      const currentQuestion = this.currentPage[0];

      let params: Partial<PayloadDrillTranslationAnswer> = {
        logId: this.logId,
        uuid: currentQuestion.uuid,
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exerciseType
      };

      if (skipped) {
        params = {
          ...params,
          skipped: true
        };
      } else {
        params = {
          ...params,
          answer: this.answer
        };
      }

      const authInfo = await getAuthInfo(this.$store.getters);

      const payload: any = {
        ...authInfo,
        ...params
      };

      const answer: any = await api.exercise.submitDrillTranslationAnswer(payload);

      if (answer.learnedStatus === 3 && answer.learnedStatus > currentQuestion.learnedStatus) {
        this.learnedBadgeCount += 1;
      }

      this.answers = {
        ...this.answers,
        [currentQuestion.uuid]: { ...answer, submittedAnswer: this.answer }
      };
    },

    onCloseIconClick() {
      exerciseNavHandler.exit(this, this.nav, navState => (this.nav = navState), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    onNextExercise() {
      nextExerciseRouter({
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid,
        ...this.nextExercise
      });
    },

    onExitCancelled() {
      this.nav = exerciseNavHandler.onExitCancelled(this.nav);
    },

    onExitConfirmed() {
      exerciseNavHandler.onExitConfirm(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    onNextQuestion() {
      this.incrementPage();
    },

    async onUserSkip() {
      this.processing = true;

      await this.logAnswer({ skipped: true });

      if (this.finalQuestion) {
        await this.endExercise();
      }

      this.incrementPage();

      this.processing = false;
    },

    async onUserResponse() {
      this.processing = true;

      await this.logAnswer({ skipped: false });

      if (this.finalQuestion) {
        await this.endExercise();
      }

      this.processing = false;
    },

    refreshAudioPlayer() {
      if (document.visibilityState === "visible") {
        console.log("refreshing audio player");
        this.audioDecoderPlayer = new AudioDecoderPlayer({ audioContext });
      }
    },

    async registerExercisePractice(problemUuids, tryAgain) {
      const authInfo = await getAuthInfo(this.$store.getters);
      const details = {
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exerciseType,
        logId: tryAgain ? undefined : this.logId,
        ...problemUuids
      };

      const apiPayload = { ...authInfo, ...details };

      const response: any = await api.exercise.startExercisePractice(apiPayload);

      this.selectedUuids = problemUuids ? problemUuids.problemUuids : [];

      this.logId = response.logId;
      this.nav.requireExitConfirmation = true;
    },

    resetState() {
      this.answer = "";
      this.answers = {};
      this.exerciseResult = undefined;
      this.learnedBadgeCount = 0;
      this.practiceMode = false;
      this.currentPageIndex = 0;
      this.feedbackFormVisible = true;
    },

    async startExercise(problemUuids) {
      this.selectedUuids = problemUuids ? problemUuids.problemUuids : [];

      const authInfo = await getAuthInfo(this.$store.getters);

      if (this.assignmentId) {
        await this.fetchAssignmentDetails();
      }

      const details = {
        assignmentId: Number(this.assignmentId) || undefined,
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exerciseType,
        ...problemUuids,
        limitedModeId: this.session?.limitedMode?.id,
        sessionId: this.session?.id
      };

      const apiPayload = { ...authInfo, ...details };
      const response: any = await api.exercise.startExercise(apiPayload);
      this.logId = response.logId;

      this.nav.requireExitConfirmation = true;
    },

    async startPractice(problemUuids, tryAgain) {
      this.processing = true;

      if (!problemUuids) {
        await this.registerExercisePractice({ problemUuids: this.selectedUuids }, tryAgain);
      } else {
        await this.registerExercisePractice(problemUuids, tryAgain);
      }

      this.practiceMode = true;
      this.displayPracticeModeToast();

      this.resetState();
      this.incrementPage();
      this.practiceMode = true;

      this.processing = false;
    },

    async startTest(tryAgain) {
      this.processing = true;

      if (tryAgain) {
        await this.startExercise();
      }

      this.selectedUuids = [];
      this.resetState();
      if (!this.limitedModeActive) {
        this.incrementPage();
      }

      this.processing = false;
    },

    async onTryAgain() {
      this.processing = true;

      const tryAgain = true;

      if (this.limitedModeActive || this.allCorrect) {
        await this.startTest(tryAgain);
      } else {
        await this.startPractice({ problemUuids: this.incorrectlyAnsweredQuestionUuids }, tryAgain);
      }
      await this.loadExercise();

      this.processing = false;
    },

    updateSelectedUuids(uuids) {
      this.selectedUuids = uuids;
    },

    updateAnswer(answer) {
      this.answer = answer;
    }
  },

  computed: {
    allCorrect() {
      return this.incorrectlyAnsweredQuestionUuids.length <= 0;
    },

    assignmentId() {
      return this.$route.params.assignmentId;
    },

    assignmentsCompleted() {
      const completedCount = this.exerciseResult?.numAssignmentsSubmitted;
      return completedCount || 0;
    },

    courseUuid() {
      return this.$route.params.courseUuid;
    },

    currentExerciseListIndex() {
      const listIndex = this.flatExerciseList.findIndex(
        (exerciseDetails: FlatExerciseListDetails) => {
          return exerciseDetails.uuid === this.exerciseUuid;
        }
      );

      return listIndex >= 0 ? listIndex : null;
    },

    currentPage() {
      if (!this.pages) {
        return;
      }

      const page = this.pages[this.currentPageIndex];

      if (!page) {
        return;
      }

      return page.body;
    },

    currentQuestionAnswered() {
      if (!this.currentPage) {
        return false;
      }

      return !!this.answers[this.currentPage[0].uuid];
    },

    currentQuestionNumber() {
      if (!this.currentPage) {
        return;
      }

      const currentQuestionUuid = this.currentPage[0].uuid;

      const currentQuestionIndex = this.questions.findIndex(question => {
        return question.body[0].uuid === currentQuestionUuid;
      });

      return currentQuestionIndex + 1;
    },

    disabled() {
      return this.processing;
    },

    drill() {
      return {
        currentIndex: 0,
        data: {
          exercise: this.questions
        },
        display: "idle",
        exercises: this.questions.map(question => {
          const uuid = question.body[0].uuid;
          return {
            question: {
              type: question.body[0].type,
              evaluatedAnswer: this.answers[uuid],
              scope: this.scope.find((scope: any) => {
                return scope.problemUuid === uuid;
              }),
              ...this.questionsByUuid[uuid]
            }
          };
        }),
        isFinished: false,
        totalNewLearnedBadge: this.learnedBadgeCount
      };
    },

    exerciseUuid() {
      return this.$route.params.exerciseUuid;
    },

    feedbackRouteParams() {
      if (!this.currentPage || !this.logId) return;

      return {
        withFeedback: Boolean(this.currentPage[0].type !== "REVIEW").toString(),
        logId: this.logId.toString()
      };
    },

    finalQuestion() {
      if (!this.currentPage || !this.pages) {
        return false;
      }

      const nextPage = this.pages[this.currentPageIndex + 1];

      if (!nextPage) {
        return false;
      }

      const currentIsQuestion = this.currentPage[0].type === "DRILL_TRANSLATION";
      const nextNotQuestion = nextPage.body[0].type !== "DRILL_TRANSLATION";

      return currentIsQuestion && nextNotQuestion;
    },

    flatExerciseList() {
      return this.$store.getters[FLAT_EXERCISE_LIST]({
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },

    footer() {
      if (!this.currentPage) {
        return;
      }

      const firstComponent = this.currentPage[0];

      switch (firstComponent.type) {
        case "EXERCISE_SCOPE":
          return "exercise-scope-buttons";
        case "REVIEW":
          return "review-buttons";
        default:
          return "translation-buttons";
      }
    },

    header() {
      return {
        courseTitle: this.course?.title,
        chapterTitle: this.chapter?.title,
        exerciseTitle: this.exercise?.title,
        sectionTitle: this.section?.title
      };
    },

    isReviewPage() {
      if (!this.currentPage) return false;

      return this.currentPage[0].type === "REVIEW";
    },

    incorrectlyAnsweredQuestionUuids() {
      const answers = Object.entries(this.answers);

      const incorrectOrSkipped = answers.reduce((accumulator: any, [uuid, accuracy]: any) => {
        if (!accuracy.isCorrect) {
          accumulator[uuid] = accuracy;
        }

        return accumulator;
      }, {});

      return Object.keys(incorrectOrSkipped);
    },

    limitedModeDetails() {
      return this.$store.getters[
        `${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.LIMITED_MODE_DETAILS}`
      ];
    },

    nextExercise() {
      if (this.currentExerciseListIndex === null) return undefined;
      return this.flatExerciseList[this.currentExerciseListIndex + 1];
    },

    numCorrectAnswers() {
      return Object.entries(this.answers).filter(([, evaluation]) => {
        return (evaluation as any).isCorrect === true;
      }).length;
    },

    numIncorrectAnswers() {
      return Object.entries(this.answers).filter(([, evaluation]) => {
        return (evaluation as any).isCorrect === false;
      }).length;
    },

    numSkippedQuestions() {
      return Object.entries(this.answers).filter(([, evaluation]) => {
        return (evaluation as any).isCorrect === null;
      }).length;
    },

    pages() {
      if (!this.exercise) {
        return;
      }

      let pages: DrillTranslationPage[] = [...this.exercise.pages];

      if (this.selectedUuids.length > 0) {
        pages = pages.filter((page: any) => {
          return (
            page.body[0].type !== "DRILL_TRANSLATION" ||
            this.selectedUuids.includes(page.body[0].uuid)
          );
        });
      } else {
        const questions = pages.filter((page: any) => {
          return page.body[0].type === "DRILL_TRANSLATION";
        });

        const firstProblemIndex = pages.findIndex((page: any) => {
          return page.body[0].type === "DRILL_TRANSLATION";
        });

        const shuffledProblems = shuffleArray(questions);

        pages.splice(firstProblemIndex, questions.length, ...shuffledProblems);
      }

      if (this.limitedModeActive) {
        pages = pages.filter(page => {
          return page.body?.[0]?.type !== "EXERCISE_SCOPE";
        });
      }

      return pages;
    },

    questions() {
      if (!this.pages) {
        return [];
      }

      let questions = this.pages.filter(page => {
        return page.body[0].type === "DRILL_TRANSLATION";
      });

      if (this.selectedUuids.length > 0) {
        questions = questions.filter(page => {
          return this.selectedUuids.includes(page.body[0].uuid);
        });
      }

      return questions;
    },

    questionsByUuid() {
      const questions: { [key: string]: any } = {};

      this.questions.forEach(question => {
        const questionObject = question.body[0];
        questions[questionObject.uuid] = questionObject;
      });

      return questions;
    },

    showFeedbackForm() {
      if (!this.currentPage) return false;

      return this.feedbackFormVisible && this.currentPage[0].type === "REVIEW";
    },

    scope() {
      if (!this.exercise) {
        return;
      }

      return this.exercise.scope;
    },

    session() {
      return this.$store.getters[
        `${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.ACTIVE_SESSION}`
      ];
    },

    totalQuestions() {
      return this.questions.length;
    },

    trophy() {
      if (!this.exerciseResult) {
        return;
      }

      const results = this.exerciseResult;
      const comment =
        results.trophy === 3
          ? this.$__("ex.trophy.gold")
          : results.trophy === 2
          ? this.$__("ex.trophy.silver", results.moreCorrectAnswers)
          : this.$__("ex.trophy.bronze", results.moreCorrectAnswers);

      return {
        comment: this.practiceMode ? "練習完了" : comment,
        correctCount: this.numCorrectAnswers,
        incorrectCount: this.numIncorrectAnswers,
        score: this.exerciseResult.trophy,
        skippedCount: this.numSkippedQuestions
      };
    }
  },
  watch: {
    limitedModeDetails: {
      handler(newValue, oldValue) {
        if (newValue && !oldValue) {
          this.limitedModeActive = true;
        } else if (!newValue && oldValue) {
          this.limitedModeActive = false;
        }
      },
      immediate: true
    }
  }
});
